import { Space } from 'antd';
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { SearchOutlined } from '@ant-design/icons';
import { Input, InputProps } from './index';
import { Select, SelectProps } from '../Select';
import { useDebounce } from '../../../hooks/useDebounce';

import cn from './styles.module.scss';

export interface SearchProps extends Omit<InputProps, 'onChange'> {
  debounceTime?: number;
  onChange?: (value: string) => void;
  value?: string;
  onPressEnter?: InputProps['onPressEnter'];
  filter?: boolean;
  filterOptions?: SelectProps & { 'data-testid'?: string };
}

export const SearchInput = ({
  value: controlledValue,
  onChange,
  onPressEnter,
  debounceTime = 800,
  filter = false,
  filterOptions,
  ...props
}: SearchProps) => {
  const [value, setValue] = useState(controlledValue);
  const [debouncedValue, setDebouncedValue] = useState(controlledValue);
  const isInitialized = useRef<boolean>();

  useEffect(() => {
    setValue(controlledValue);
  }, [controlledValue]);

  useEffect(() => {
    if (onChange && isInitialized.current && debouncedValue !== controlledValue) {
      onChange(value as string);
    }
    isInitialized.current = true;
  }, [debouncedValue]);

  useDebounce(
    () => { setDebouncedValue(value); },
    debounceTime,
    [value],
  );

  const icon = useMemo(() => {
    const key = filter ? 'suffix' : 'prefix';

    return {
      [key]: <SearchOutlined />,
    };
  }, [filter]);

  const handleChange = ({ target: { value: newValue } }: any) => {
    setValue(newValue);
  };

  const InputComponent = useMemo(() => (
    <Input
      placeholder="Search"
      value={value}
      onChange={handleChange}
      onPressEnter={(event) => {
        if (onPressEnter) {
          onPressEnter(event);
        }
      }}
      {...icon}
      {...props}
    />
  ), [props, icon, handleChange, value, onPressEnter]);

  if (filter) {
    return (
      <Space.Compact>
        <Select
          size={props.size || 'large'}
          className={classNames(cn.filter)}
          showSearch={false}
          classNames={{
            content: cn.content,
          }}
          {...filterOptions}
        />
        <div style={{ width: '100%' }}>
          {InputComponent}
        </div>
      </Space.Compact>
    );
  }

  return InputComponent;
};
